import {computed, inject, Injectable, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {catchError, of} from 'rxjs';
import {CountryCode, DirectusService, DirectusTranslationPipe} from '../directus';
import {TranslocoService} from '@jsverse/transloco';

@Injectable({providedIn: 'root'})
export class CountryService {
  private readonly directusService = inject(DirectusService);
  private readonly translocoService = inject(TranslocoService);
  private readonly directusTranslationPipe = new DirectusTranslationPipe();

  private readonly currentLanguage = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  public readonly countriesSorted = computed(() => {
    const countries = this.allCountries();
    const language = this.currentLanguage();
    return this._sortCountries(countries, language);
  });

  readonly allCountries = toSignal(
    this.directusService.loadCountryCodes().pipe(
      catchError((err) => {
        console.error('Failed to load countries from Directus:', err);
        return of([] as CountryCode[]);
      }),
    ),
    {initialValue: [] as CountryCode[]},
  );

  getCountryNameByCode(code: string, lang?: string): string {
    const currentLang = lang ?? this.translocoService.getActiveLang();
    const country = this.allCountries().find((c) => c.isoCode === code);
    if (!country) {
      return code;
    }
    return (
      this.directusTranslationPipe.transform(country.translations, currentLang) || country.isoCode
    );
  }

  getCountryDisplayName(country: CountryCode, lang?: string): string {
    const currentLang = lang ?? this.translocoService.getActiveLang();
    return (
      this.directusTranslationPipe.transform(country.translations, currentLang) ?? country.isoCode
    );
  }

  getFilteredAndSortedCountries(
    countryCodesSignal: Signal<string[]>,
    languageSignal: Signal<string>,
  ): Signal<CountryCode[]> {
    return computed(() => {
      const codes = countryCodesSignal();
      const lang = languageSignal();
      const all = this.allCountries();

      const filtered = all.filter((country) => codes.includes(country.isoCode));
      return this._sortCountries(filtered, lang);
    });
  }

  getCountryFlagUrl(countryCode: string): string {
    return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
  }

  getSortedCountries(countries: CountryCode[]): CountryCode[] {
    return this._sortCountries(countries, this.translocoService.getActiveLang());
  }

  private _sortCountries(countries: CountryCode[], lang: string): CountryCode[] {
    return [...countries].sort((a, b) => {
      const nameA = this.directusTranslationPipe.transform(a.translations, lang) || a.isoCode;
      const nameB = this.directusTranslationPipe.transform(b.translations, lang) || b.isoCode;
      return nameA.localeCompare(nameB);
    });
  }
}
